<template>
  <div>
    <page-header
      :actions="headerActionConfig"
      left-side-class="col-xl-4 pb-2"
      :page="headerConfig"
      right-side-class="col-xl-8 align-items-end d-flex pb-4 justify-content-end"
    />

    <board :details="detailsBoard" />

    <div class="mt-4">
      <expectation
        ref="answers"
        :input.sync="model.answers"
        :is-read-only="isReadOnly"
        @add="addAnswer"
        @quit="quitAnswer"
      />
    </div>

    <div class="mt-4">
      <sections
        ref="sections"
        :answers="model.answers"
        :input.sync="model.sections"
        :is-read-only="isReadOnly"
        :status="model.status"
      />
    </div>
    <div class="mt-4">
      <additional-questions
        ref="additionalQuestions"
        :input.sync="model.additionalQuestions"
        :is-read-only="isReadOnly"
      />
    </div>
    <div class="mt-4">
      <evaluation
        ref="evaluation"
        v-model="model.evaluationConfig"
        :is-read-only="isReadOnly"
        :sections="model.sections"
      />
    </div>
  </div>
</template>

<script>
import alert from '@/mixins/alert.mixin';

import PageHeader from 'PageHeader';
import Board from 'Board';
import Expectation from './components/Expectation';
import Sections from './components/Sections';
import AdditionalQuestions from './components/AdditionalQuestions';
import Evaluation from './components/Evaluation.vue';

import { save, getById } from '@/modules/rubric/api.js';

import { diff, copy } from '@/utils/objUtils';

export default {
  name: 'RubricAdd',
  components: {
    PageHeader,
    Board,
    Expectation,
    Sections,
    AdditionalQuestions,
    Evaluation,
  },
  mixins: [alert],
  data() {
    return {
      headerConfig: {
        title: 'Creación de Configuración de Rúbrica',
        icon: 'fas fa-file-invoice',
        routes: [
          'Proceso de Competencias',
          'Creación de Configuración de Rúbrica',
        ],
      },
      model: {},
      initialModel: {},
    };
  },
  computed: {
    detailsBoard() {
      return [
        [
          {
            label: 'Año Académico:',
            icon: 'fas fa-calendar-minus',
            data: this.model?.academicYear?.name,
            cols: 'col-md-3 col-12',
          },
          {
            label: 'Área:',
            icon: 'fa fa-file-invoice',
            data: this.model?.area?.name,
            cols: 'col-md-3 col-12',
          },
          {
            label: 'Servicios a Ofrecer:',
            icon: 'fas fa-file-invoice',
            data: this.model?.service?.name,
            cols: 'col-md-3 col-12',
          },
          {
            label: 'Tipo de Evaluación:',
            icon: 'fas fa-file-invoice',
            data: this.model?.type?.name,
            cols: 'col-md-3 col-12',
          },
        ],
      ];
    },
    headerActionConfig() {
      return [
        {
          text: 'Regresar',
          icon: 'far fa-arrow-circle-left',
          variant: 'outline-success',
          click: () => this.$router.push({ name: 'RubricSetting' }),
        },
        {
          text: 'Someter',
          icon: 'fas fa-file-check',
          variant: 'outline-success',
          visible: Boolean(this.id) && !this.model.isSubmitted,
          click: () => this.submit(),
        },
        {
          text: 'Guardar',
          icon: 'fas fa-save',
          variant: 'success',
          visible: !this.model.isSubmitted,
          click: () => this.save(),
        },
      ];
    },
    id() {
      const { id } = this.$route.params;
      if (!id) return null;
      return Number(id);
    },
    isReadOnly() {
      return false;
    },
    modelOnlyValues() {
      const answers = this.model.answers.map((x) => ({
        ...x,
        valid: true,
        expanded: false,
      }));
      const sections = this.model.sections.map((x) => ({
        ...x,
        valid: true,
        expanded: false,
        score: 0,
        questions: x.questions.map((q) => ({
          ...q,
          valid: true,
          expanded: false,
        })),
      }));
      const additionalQuestions = this.model.additionalQuestions.map((x) => ({
        ...x,
        valid: true,
        expanded: false,
      }));
      return { ...this.model, answers, sections, additionalQuestions };
    },
  },
  async mounted() {
    await this.init();
  },
  methods: {
    getEmptyModel() {
      let { model } = this.$route.params;

      if (model) {
        const { academicYear, area, service } = model;
        sessionStorage.setItem(
          'rubricAddBoard',
          JSON.stringify({
            academicYear,
            area,
            service,
          })
        );
      } else {
        const sessionModel = sessionStorage.getItem('rubricAddBoard');
        if (sessionModel) model = JSON.parse(sessionModel);
      }
      return {
        id: null,
        academicYear: null,
        area: null,
        service: null,
        status: null,
        type: null,
        evaluationConfig: {
          isSpecialEvaluatorRequired: null,
          specialEvaluatorSections: [],
        },
        answers: [],
        sections: [],
        additionalQuestions: [],
        ...model,
      };
    },
    async init() {
      const model = this.id ? await this.get(this.id) : this.getEmptyModel();
      this.model = copy(model);
      this.initialModel = copy(model);
    },
    async get(id) {
      const { data } = await getById(id).catch((error) =>
        this.ShowToast('Error', error.response.data.message, 'error')
      );
      data.answers.forEach((x) => {
        x.valid = true;
        x.expanded = false;
      });
      data.sections.forEach((x) => {
        x.valid = true;
        x.expanded = false;
        x.score = 0;
        x.questions.forEach((q) => {
          q.valid = true;
          q.expanded = false;
          const texts = q.name.split('<br/>');
          q.name = texts[0]?.trim();
          q.notes = texts[1]?.trim();
        });
      });
      data.additionalQuestions.forEach((x) => {
        x.valid = true;
        x.expanded = false;
      });
      return data;
    },
    async save(submitted = false) {
      if (this.model.sections.length === 0) {
        this.ShowToast(
          'Error',
          'Es necesario seleccionar al menos una sección para guardar.',
          'error'
        );
        return;
      }
      const valid = await this.validate();

      if (!valid) {
        this.ShowToast(
          'Alerta',
          'Favor completar la información solicitada.',
          'error'
        );
        return;
      }

      if (
        this.model.sections.length ===
        this.model.evaluationConfig.specialEvaluatorSections.length
      ) {
        this.ShowToast(
          'Alerta',
          'Se requiere al menos una sección dirigida a evaluadores no especializados.',
          'error'
        );

        return;
      }

      if (submitted) {
        const confirmed = await this.ShowCancelModal(
          'Confirmación',
          '¿Está seguro que desea someter la rúbrica?',
          'Si',
          'No'
        );

        if (!confirmed) return;
      }
      const payload = {
        submitted,
        id: this.model.id,
        academicYearId: this.model.academicYear.id,
        areaId: this.model.area.id,
        serviceId: this.model.service.id,
        typeId: this.model.type.id,
        answers: this.model.answers,
        sections: this.model.sections,
        additionalQuestions: this.model.additionalQuestions,
        evaluationConfig: this.model.evaluationConfig,
      };
      const { data: id } = await save(payload).catch((error) =>
        this.ShowToast('Error', error.response.data.message, 'error')
      );

      this.ShowToast(
        'Proceso exitoso',
        'Los cambios fueron guardados exitosamente',
        'success'
      );

      if (submitted) {
        const copied = copy(this.model);
        this.initialModel = copied;
        this.model = copied;
        this.$router.push({
          name: 'RubricPreview',
          params: { id },
        });
        return;
      }
      const added = !this.model.id;
      const data = await this.get(id);
      this.initialModel = data;
      this.model = data;
      if (added) this.$router.push({ name: 'RubricEdit', params: { id } });
      return true;
    },
    async submit() {
      if (this.model.answers.length < 3) {
        this.ShowToast(
          'Error',
          'Es necesario seleccionar al menos 3 expectativas para someter la rúbrica.',
          'error'
        );
        return;
      }

      if (this.model.sections.length === 0) {
        this.ShowToast(
          'Error',
          'Es necesario seleccionar al menos una sección para guardar.',
          'error'
        );
        return;
      }

      const total = this.model.sections
        .map(({ score }) => score)
        .reduce((total, score) => total + score);
      if (total !== 100) {
        this.ShowToast(
          'Error',
          `La sumatoria de las secciones deben ser igual a 100 para someter la rúbrica. <p class="mt-1">Sumatoria actual: ${total}</p>`,
          'error'
        );
        return;
      }
      this.save(true);
    },
    async validate() {
      const validA = await this.$refs['answers'].validate();
      const validS = await this.$refs['sections'].validate();
      const validAQ = await this.$refs['additionalQuestions'].validate();
      const validAE = await this.$refs['evaluation'].validate();
      return [validA, validS, validAQ, validAE].every((x) => x);
    },
    addAnswer({ id, text, color }) {
      const questions = this.model.sections.flatMap((x) => x.questions);
      questions.forEach((x) =>
        x.answers.push({
          answerId: id,
          text,
          color,
          value: null,
        })
      );
    },
    quitAnswer({ id }) {
      const questions = this.model.sections.flatMap((x) => x.questions);
      questions.forEach((x) => {
        const index = x.answers.findIndex((y) => y.answerId === id);
        x.answers.splice(index, 1);
      });
    },
  },
  async beforeRouteLeave(_, from, next) {
    const isEditing = diff(this.model, this.initialModel, {
      exclude: ['valid', 'expanded', 'score'],
      deep: true,
    });
    if (isEditing) {
      const name = '<b>Rúbrica</b>';
      const saveChangesText = `<span class="text-success">guardar los cambios</span>`;
      const discardText = `<span class="text-danger">descartarlos</span>`;
      const { value: confirmed, dismiss } = await this.ShowCancelModalClosable(
        'Confirmación',
        `Ha modificado esta ${name}. Puede ${saveChangesText}, ${discardText} o cerrar este mensaje para continuar editando.`,
        `<i class="fas fa-save mr-1"></i> Guardar`,
        'Salir',
        true
      );
      const cancelled = ['close', 'backdrop'].includes(dismiss);
      if (cancelled) return false; //keep in the screen if don't want to save but stay
      if (confirmed) {
        const saved = await this.save();
        if (!saved) return false;
      }
    }
    next();
  },
};
</script>

<style scoped lang="scss">
@import '@/scss/brand_variables.scss';
.bg-custom-gray {
  background-color: #f7f7f7;
}
</style>
